/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DeliveryTable.module.scss';

import { type DeliveryTableAction, type DeliveryTableProps } from './DeliveryTable.types';

const RowCta = ({ action }: { action: DeliveryTableAction }) => {
  const common = { type: 'transparent' as const, size: 'lg' as const, suffixIcon: 'arrow-14' };

  if (action.route) {
    return (
      <Button {...common} route={action.route}>
        {action.label}
      </Button>
    );
  }

  return (
    <Button {...common} href={action.href ?? '#'}>
      {action.label}
    </Button>
  );
};

export const DeliveryTable = ({
  title,
  description,
  rows,
  numbered = false,
  align = 'left',
  background,
  className,
}: DeliveryTableProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.1 });

  return (
    <Section background={background} className={cn(styles.root, className)}>
      <div className={cn(styles.head, styles[`head_${align}`])}>
        <TitleBlock align={align} title={title} description={description} className={styles.titleBlock} />
      </div>

      <div ref={ref} className={cn(styles.list, numbered && styles.numbered, revealed && styles.listIn)}>
        {rows.map((row, index) => (
          <div key={row.name} className={styles.row} style={{ '--delay': `${index * 60}ms` } as CSSProperties}>
            {numbered && <span className={styles.num}>{String(index + 1).padStart(2, '0')}</span>}

            <div className={styles.main}>
              <div className={styles.nameRow}>
                <span className={styles.name}>{row.name}</span>
                {row.tag && <span className={styles.tag}>{row.tag}</span>}
              </div>

              <p className={styles.desc}>{row.desc}</p>
            </div>

            <div className={styles.ctas}>
              {row.actions.map((action) => (
                <RowCta key={action.label} action={action} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};
